import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { InstanceTypes } from '../../models/instance-types';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { merge, Observable, of, Subject } from 'rxjs';
import { CompaniesService } from '../../services/companies.service';
import { ClientHomePage } from '../../models/client';
import { AclService } from '../../services/acl.service';
import { ClientSelectionState } from '../../state/ClientSelectionState/ClientSelection.state';
import { Select, Store } from '@ngxs/store';
import { UserManagementService } from '../../services/user-management.service';
import { UserTimeclockMapping } from '../../models/person';

@Component({
  selector: 'lib-client-home',
  templateUrl: './client-home.component.html',
  styleUrls: ['./client-home.component.scss'],
  standalone: false
})
export class ClientHomeComponent implements OnInit, OnDestroy {
  salutation: string;
  currentInstanceType: InstanceTypes;
  homepageModel: ClientHomePage;
  @Select(ClientSelectionState.clientCode)
  clientCode: Observable<string>;
  canManageUsers = false;
  canViewReports = false;
  canViewPayroll = false;
  canViewCsrFields = false;
  canViewPeople = false;
  @Select(ClientSelectionState.canEnableChangeRequests)
  canViewDemographicRequests: Observable<boolean>;

  hasAddressPermission = false;
  hasDirectDepositPermission = false;

  hasTimeOffPermissions = false;
  timeclockMapping: UserTimeclockMapping | null;
  companySwipeclockIdSynched = false;

  public get isClientPortal() {
    return this.env.instanceType === InstanceTypes.ClientPortal;
  }

  private destroy$ = new Subject<void>();

  get columnClasses() {
    let currentBoxes = 0;
    if (this.canViewPayroll) {
      currentBoxes += 2;
    }
    if (this.canViewPeople && !this.canViewPayroll) {
      currentBoxes++;
    }
    if (this.canViewReports) {
      currentBoxes++;
    }
    if (this.canManageUsers && !this.canViewPayroll && !this.canViewPeople) {
      currentBoxes++;
    }
    return {
      'col-lg-12': currentBoxes === 1,
      'col-lg-6': currentBoxes === 2,
      'col-lg-4': currentBoxes === 3,
      'col-lg-3': currentBoxes === 4
    };
  }

  constructor(
    private authService: AuthService,
    @Inject('env') private env: any,
    private companyService: CompaniesService,
    private aclService: AclService,
    private store: Store,
    private userManagementService: UserManagementService
  ) {
    this.currentInstanceType = env.instanceType;
  }

  ngOnInit() {
    const hr = new Date().getHours();

    if (hr >= 0 && hr <= 11) {
      this.salutation = 'Good Morning';
    } else if (hr >= 12 && hr <= 16) {
      this.salutation = 'Good Afternoon';
    } else if (hr >= 17 && hr <= 23) {
      this.salutation = 'Good Evening';
    }

    this.salutation += ' ' + this.getFirstName();

    this.store
      .select(ClientSelectionState.clientCode)
      .pipe(
        takeUntil(this.destroy$),
        mergeMap(x => {
          if (x != null) {
            return this.companyService.getCompanyHomepage(x).pipe(
              map(homepage => {
                return {
                  clientCode: x,
                  homepage
                };
              })
            );
          } else {
            return of(null);
          }
        }),
        mergeMap(x => {
          if (x != null) {
            const userId = this.authService.getUser().UserId;
            return this.userManagementService.getUserTimekeepingConfig(userId, x.clientCode).pipe(
              map(result => {
                return {
                  ...x,
                  timekeepingConfig: result
                };
              })
            );
          } else {
            return of(null);
          }
        })
      )
      .subscribe(x => {
        if (x != null) {
          this.homepageModel = x.homepage;
          this.timeclockMapping = x.timekeepingConfig;
          this.companyService.getSwipeclockMapping(x.clientCode).subscribe(x => {
            this.companySwipeclockIdSynched = !!x;
          });
          const userId = this.authService.getUser().UserId;
          this.aclService.HasMinimumRoleByName(userId, 'CSR1').subscribe(result => {
            if (result) {
              this.canManageUsers = true;
              this.canViewPayroll = true;
              this.canViewReports = true;
              this.canViewPeople = true;
              this.canViewCsrFields = true;
              this.hasTimeOffPermissions = true;
              this.hasAddressPermission = true;
              this.hasDirectDepositPermission = true;
            } else {
              this.aclService
                .HasMinimumRoleByName(userId, 'Client Portal Admin', x.clientCode)
                .subscribe(result => {
                  this.canManageUsers = result;
                });
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'Reports', x.clientCode)
                .subscribe(result => {
                  this.canViewReports = result;
                });
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'RunPayroll', x.clientCode)
                .subscribe(result => {
                  this.canViewPayroll = result;
                });
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'EditPeople', x.clientCode)
                .subscribe(result => {
                  this.canViewPeople = result;
                });
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'addressChange', x.clientCode)
                .subscribe(result => {
                  this.hasAddressPermission = result;
                });
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'directDepositChange', x.clientCode)
                .subscribe(result => {
                  this.hasDirectDepositPermission = result;
                });
              this.aclService
                .AccessCheck(userId, 'Client Portal Admin', 'timeOff', x.clientCode)
                .subscribe(result => {
                  this.hasTimeOffPermissions = result;
                });
            }
          });
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  getFirstName() {
    const user = this.authService.user;
    if (user && user.GivenName) {
      return user.GivenName;
    }
    return '';
  }
}
